
import React, { useState } from 'react';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import ReCAPTCHA from "react-google-recaptcha";
import { generateVerificationCode, sendVerificationCode } from '@/services/api';
import { useNavigate } from 'react-router-dom';

interface SignupFormProps {
  isSubmitting: boolean;
  setCaptchaVerified: (verified: boolean) => void;
  captchaVerified: boolean;
}

const SignupForm: React.FC<SignupFormProps> = ({ 
  isSubmitting, 
  setCaptchaVerified,
  captchaVerified
}) => {
  const navigate = useNavigate();
  
  const [signupData, setSignupData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    referrerCode: ''
  });
  
  const handleSignupInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // If this is the phone field, format it for display but store only the digits
    if (name === 'phone') {
      // For display, allow formatting while typing
      setSignupData(prev => ({ 
        ...prev, 
        [name]: value 
      }));
    } else {
      setSignupData(prev => ({ ...prev, [name]: value }));
    }
  };
  
  const handleCaptchaChange = (value: string | null) => {
    setCaptchaVerified(!!value);
  };
  
  const handleSignupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!captchaVerified) {
      toast.error("Please verify you are not a robot");
      return;
    }
    
    try {
      // Generate verification code
      const verificationCode = generateVerificationCode();
      
      // Format the phone number to contain only digits
      const formattedPhone = signupData.phone.replace(/\D/g, '');
      
      // Send to webhook with only numeric digits in phone number
      await sendVerificationCode({
        ...signupData,
        phone: formattedPhone
      }, verificationCode);
      
      // Store in localStorage for verification step (with formatted phone)
      localStorage.setItem('verificationData', JSON.stringify({
        ...signupData,
        phone: formattedPhone, // Store only digits for later use
        verificationCode
      }));
      
      // Navigate to verification page
      navigate('/verify');
      
      toast.success("Verification code sent to your phone");
    } catch (error) {
      console.error("Signup error:", error);
      toast.error("Failed to create account. Please try again.");
    }
  };

  return (
    <Card className="glass-panel">
      <CardHeader>
        <CardTitle className="text-2xl font-bold text-center">Create Account</CardTitle>
        <CardDescription className="text-center text-base">
          Join our referral program and start earning
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSignupSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label htmlFor="firstName" className="text-sm font-medium">
                First Name
              </label>
              <Input
                id="firstName"
                name="firstName"
                placeholder="John"
                required
                className="input-style"
                value={signupData.firstName}
                onChange={handleSignupInputChange}
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="lastName" className="text-sm font-medium">
                Last Name
              </label>
              <Input
                id="lastName"
                name="lastName"
                placeholder="Doe"
                required
                className="input-style"
                value={signupData.lastName}
                onChange={handleSignupInputChange}
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <label htmlFor="email" className="text-sm font-medium">
              Email
            </label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="john@example.com"
              required
              className="input-style"
              value={signupData.email}
              onChange={handleSignupInputChange}
            />
          </div>
          
          <div className="space-y-2">
            <label htmlFor="phone" className="text-sm font-medium">
              Phone Number
            </label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              placeholder="(123) 456-7890"
              required
              className="input-style"
              value={signupData.phone}
              onChange={handleSignupInputChange}
            />
            <p className="text-xs text-muted-foreground">
              Format: (123) 456-7890
            </p>
          </div>
          
          <div className="space-y-2">
            <label htmlFor="referrerCode" className="text-sm font-medium">
              Your Referrer Code
            </label>
            <Input
              id="referrerCode"
              name="referrerCode"
              placeholder="Create your unique code"
              required
              className="input-style"
              value={signupData.referrerCode}
              onChange={handleSignupInputChange}
            />
            <p className="text-xs text-muted-foreground">
              DO NOT share this code with anyone
            </p>
          </div>
          
          <div className="flex justify-center my-4">
            <ReCAPTCHA
              sitekey="6LcC_uwqAAAAAAb0hT4T1CM-fT2szm08CvXao-QK"
              onChange={handleCaptchaChange}
              theme="dark"
              size="normal"
            />
          </div>
          
          <Button
            type="submit"
            className="w-full h-12 glass-button text-lg"
            disabled={isSubmitting || !captchaVerified}
          >
            {isSubmitting ? "Processing..." : "Create Account"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default SignupForm;
